/**
 * @module ThrottleRequests
 */

import { trace } from 'owa-trace';

/**
 * @type
 * A type declaration for the throttle callback
 */
type ThrottleCallback = (isThrottled: boolean) => void;

/**
 * @class
 * A class for throttling requests
 */
export default class ThrottleRequests {
    /**
     * @private
     * A member to store the list of throttle callbacks
     *
     * We are using a queue to throttle web service calls.
     * The queue is an array of callback functions to set State in the calling component.
     * When the state is changed, the component will be rendered again.
     * We only allow certain concurrent requests to server. If more than max number,
     * the requests will be pushed to the queue. We should handle success and error
     * of the requests to process next request in queue.
     */
    private throttleCallbacks: ThrottleCallback[] = [];

    /**
     * @private
     * A member to hold the maximum number of concurrent requests
     */
    private maxConcurrentRequests: number;

    /**
     * @private
     * A member to hold the number of activeRequests
     */
    private activeRequests: number = 0;

    /**
     * @private
     * A member holding the number of processed requests
     */
    private processedRequests: number = 0;

    /**
     * @private
     * A member holding number of last processed requests
     *
     * This is used for resetting the active requests when the watch dog timer runs out
     */
    private lastProcessedRequests: number = 0;

    /**
     * @private
     * A member to hold the watch dog timer interval
     *
     * I have noticed that browsers may set requests to pending for long time and never process them.
     * Image load and error events are also not always triggered due to various reasons.
     * In case we ran into any issues, a watch dog timer will process the queue after certain time no action.
     */
    private watchDogTimerInterval: number;

    /**
     * @private
     * A member to hold the watch dog timer
     */
    private watchDogTimer: number = null;

    /**
     * @private
     * A function to handle the actions of a watchdog
     * @returns nothing
     */
    private watchDogAction(): void {
        if (this.throttleCallbacks.length > 0) {
            if (this.processedRequests === this.lastProcessedRequests) {
                // The queue has not been processed for 5 seconds, force to process items.
                this.activeRequests = 0;

                this.traceInfo('Watch dog is processing the queue.');

                this.processNextRequest();
            } else {
                this.traceInfo('Watch dog is watching.');

                this.lastProcessedRequests = this.processedRequests;
            }
        } else {
            // There is nothing in the queue, stop watch dog
            window.clearInterval(this.watchDogTimer);
            this.watchDogTimer = null;

            this.traceInfo('Stop watch dog.');
        }
    }

    /**
     * @private
     * A custom traceInfo method to log details with current status of the processing
     * @param {string} info - The information string
     * @returns nothing
     */
    private traceInfo(info: string): void {
        trace.info(
            `ThrottleRequests:active=${this.activeRequests};throttled=${this.throttleCallbacks.length};processed=${this.processedRequests};lastProcessed=${this.lastProcessedRequests};info=${info}`
        );
    }

    /**
     * @public
     * @constructor
     * @param {number} [maxConcurrentRequests = 5] - An optional max concurrent requests
     * @param {number} [watchDogTimerInterval = 5000] - A time interval for watchdog in milliseconds
     */
    public constructor(maxConcurrentRequests: number = 5, watchDogTimerInterval: number = 5000) {
        this.maxConcurrentRequests = maxConcurrentRequests;
        this.watchDogTimerInterval = watchDogTimerInterval;
    }

    /**
     * @public
     * To process the next request in the queue
     * @returns nothing
     */
    public processNextRequest(): void {
        this.processedRequests++;

        if (this.throttleCallbacks.length === 0) {
            // The queue is empty
            if (this.activeRequests > 0) {
                this.activeRequests--;
            }

            this.traceInfo('Queue is empty.');
        } else {
            // Get first callback from queue, and set isThrottled to false.
            // This callback will set component's state, and trigger the render function.
            const throttleCallback: ThrottleCallback = this.throttleCallbacks.shift();
            throttleCallback(false /* isThrottled */);

            this.traceInfo('Process next.');
        }
    }

    /**
     * @public
     * To register a throttle with a callback
     * @param {ThrottleCallback} throttleCallback - A callback function
     * @returns nothing
     */
    public registerCallback(throttleCallback: ThrottleCallback): void {
        if (throttleCallback == null) {
            throw new Error('A null callback is registered to Throttle Thumbnails queue.');
        }

        if (this.activeRequests < this.maxConcurrentRequests) {
            // Process now
            throttleCallback(false /* isThrottled */);

            this.activeRequests++;

            this.processedRequests++;

            this.traceInfo('Process now.');
        } else {
            // The active requests are more than max allowed concurrent requests.
            // Push the callback to queue, and set component's isThrottled state to true.
            throttleCallback(true /* isThrottled */);
            this.throttleCallbacks.push(throttleCallback);
            this.traceInfo('Pushes to queue.');

            if (!this.watchDogTimer) {
                this.watchDogTimer = window.setInterval(() => {
                    this.watchDogAction();
                }, this.watchDogTimerInterval);

                this.traceInfo('Start watch dog.');
            }
        }
    }

    /**
     * @public
     * To remove the throttle callback from the queue
     * @param {ThrottleCallback} throttleHandler - Same callback function that is used to register
     * @returns nothing
     */
    public unregisterCallback(throttleHandler: ThrottleCallback): void {
        const index = this.throttleCallbacks.indexOf(throttleHandler);
        this.throttleCallbacks.splice(index, 1);

        // If thumbnail components are removing from DOM, we may never receive image load or error events
        // that we are used to process the queue. So manually set active requests to 0 to avoid issues.
        this.activeRequests = 0;

        this.traceInfo('unregisterComponent:' + index);
    }

    /**
     * @public
     * To reset the throttling instance, clears callback queue and
     * @returns nothing
     */
    public clearQueue = (): void => {
        this.throttleCallbacks.length = 0;
        this.activeRequests = 0;
    };
}
